import { BOOKING_INELIGIBLE_ERROR_PREFIX, BOOKING_SESSION_EXPIRED } from "@internal/constants"

// A terminal known-data eligibility rejection from bookAppointment: the BFF marks these with a
// prefix on the FORBIDDEN message, which is the only signal safe to treat as terminal. A bare
// FORBIDDEN is NOT terminal in general — the BFF's error mapping re-emits an upstream 403 (from an
// Auth0 token rotation, a gateway RBAC blip, etc.) as FORBIDDEN too, so classifying every FORBIDDEN
// as ineligibility would permanently strand a member on a transient upstream failure. Returns the
// reason id for the prefixed case (→ not-eligible screen), else null (→ retryable inline error).
// Duck-typed on `data.code` since the error crosses the tRPC client boundary. (MEM-771)
export function bookingIneligibilityReason(error: unknown): string | null {
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data?: { code?: string } }).data
    const message = (error as { message?: unknown }).message
    if (
      data?.code === "FORBIDDEN" &&
      typeof message === "string" &&
      message.startsWith(BOOKING_INELIGIBLE_ERROR_PREFIX)
    ) {
      return message.slice(BOOKING_INELIGIBLE_ERROR_PREFIX.length)
    }
  }
  return null
}

// bookAppointment answers PRECONDITION_FAILED when a required consent isn't signed (recoverable →
// route back to consent). Duck-typed on `data.code`, since the error crosses the tRPC client boundary.
export function isBookingConsentRequired(error: unknown): boolean {
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data?: { code?: string } }).data
    return data?.code === "PRECONDITION_FAILED"
  }
  return false
}

export function bookingBlockedMeta(step: string, message: string) {
  return {
    errorReport: {
      message: `Telemedicine booking: ${message}`,
      tags: { feature: "telemedicine_booking", block_step: step }
    }
  }
}

// Routing moved between loading the slot and redeeming it. The token must not be retried; the
// member picks a slot from a fresh availability read.
export function isBookingSessionExpired(error: unknown): boolean {
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data?: { code?: string } }).data
    const message = (error as { message?: unknown }).message
    return data?.code === "CONFLICT" && message === BOOKING_SESSION_EXPIRED
  }
  return false
}

export function isBookingAuthorizationConflict(error: unknown): boolean {
  if (error && typeof error === "object" && "data" in error) {
    return (error as { data?: { code?: string } }).data?.code === "CONFLICT"
  }
  return false
}
